
import { useEffect, useRef, useState } from "react";
import { Modal } from "antd";
import { apiUrl } from "../../config/config";
import "./websiteAnnouncement.css";

const API_URL = `${apiUrl}/api/custom-popup`;

const WebsiteAnnouncement = () => {
  const [popup, setPopup] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("idle");
  const [submitMessage, setSubmitMessage] = useState("");
  const submitting = useRef(false);

  const handleEmailSubmit = async (event) => {
    event.preventDefault();
    if (submitting.current || submitStatus === "success") return;
    const form = event.currentTarget;
    const emailInput = form.querySelector('input[name="email"], input[type="email"]');
    if (!emailInput) {
      setSubmitStatus("error");
      setSubmitMessage("The email field is unavailable. Please try again later.");
      return;
    }
    emailInput.type = "email";
    emailInput.required = true;
    emailInput.value = emailInput.value.trim();
    if (!form.reportValidity()) return;

    submitting.current = true;
    setSubmitStatus("loading");
    setSubmitMessage("");
    try {
      const response = await fetch(`${apiUrl}/api/email-query`, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ email: emailInput.value }),
      });
      const result = await response.json().catch(() => null);
      if (!response.ok || result?.success === false) {
        const emailError = result?.errors?.email;
        throw new Error(
          (Array.isArray(emailError) ? emailError[0] : emailError) ||
          result?.message || "Could not send your email. Please try again."
        );
      }
      setSubmitStatus("success");
      setSubmitMessage(result?.message || "Thank you! Your email has been submitted.");
    } catch (error) {
      setSubmitStatus("error");
      setSubmitMessage(error.message || "Could not send your email. Please try again.");
    } finally {
      submitting.current = false;
    }
  };

  useEffect(() => {
    let mounted = true;
    let timer;

    const controller = new AbortController();

    const fetchPopup = async () => {
      try {
        const response = await fetch(API_URL, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Popup API Error: ${response.status}`);
        }

        const result = await response.json();

        const popups = Array.isArray(result)
          ? result
          : Array.isArray(result?.data)
          ? result.data
          : result?.data
          ? [result.data]
          : result?.id
          ? [result]
          : [];

        const activePopup = popups.find(
          (item) =>
            item.status === true ||
            item.status === 1 ||
            item.status === "1"
        );

        if (!mounted || !activePopup) return;

        setPopup(activePopup);

        timer = window.setTimeout(() => {
          if (mounted) {
            setIsOpen(true);
          }
        }, 3000);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Failed to load website popup:", error);
        }
      }
    };

    fetchPopup();

    return () => {
      mounted = false;
      controller.abort();
      window.clearTimeout(timer);
    };
  }, []);

  const closeModal = () => {
    setIsOpen(false);
  };

  if (!popup) return null;

  const rawImage = typeof popup.img === "string" ? popup.img.trim() : "";
  const imageUrl = rawImage.match(/^\[[^\]]*\]\(([^)]+)\)$/)?.[1] || rawImage;
  const hasImage = Boolean(imageUrl) && !imageFailed;

  const primaryColor = /^#[0-9a-fA-F]{6}$/.test(
    popup.bg_color || ""
  )
    ? popup.bg_color
    : "#1cc0d9";

  const buttonTextColor = /^#[0-9a-fA-F]{6}$/.test(
    popup.text_color || ""
  )
    ? popup.text_color
    : "#ffffff";

  return (
    <Modal
      rootClassName="website-announcement"
      open={isOpen}
      onCancel={closeModal}
      footer={null}
      centered
      width={hasImage ? 860 : 460}
      zIndex={11000}
      style={{
        "--popup-primary": primaryColor,
        "--popup-button-text": buttonTextColor,
      }}
    >
      <div className={`website-announcement-layout${hasImage ? " website-announcement-layout-with-image" : ""}`}>
        {hasImage && (
          <div className="website-announcement-image">
            <img
              src={imageUrl}
              alt={popup.title || "Announcement"}
              onError={() => setImageFailed(true)}
            />
          </div>
        )}
        <form className="website-announcement-content" onSubmit={handleEmailSubmit} aria-busy={submitStatus === "loading"}>

        <div className="website-announcement-accent" />

        {popup.title && (
          <h2 className="website-announcement-title">
            {popup.title}
          </h2>
        )}

        {popup.subtitle && (
          <h3 className="website-announcement-subtitle">
            {popup.subtitle}
          </h3>
        )}

        {popup.paragraph && (
          <p className="website-announcement-message">
            {popup.paragraph}
          </p>
        )}

        {popup.custom_field && (
          <div
            className="website-announcement-custom-field"
            dangerouslySetInnerHTML={{
              __html: popup.custom_field,
            }}
          />
        )}

        {popup.coupon_text && (
          <div className="website-announcement-coupon">
            <span>{popup.coupon_text}</span>
          </div>
        )}

        {popup.button_text && (
          <button
            type={popup.custom_field ? "submit" : "button"}
            className="website-announcement-button"
            onClick={popup.custom_field ? undefined : closeModal}
            disabled={submitStatus === "loading" || submitStatus === "success"}
          >
            {submitStatus === "loading" ? "Sending..." : submitStatus === "success" ? "Sent" : popup.button_text}
          </button>
        )}

        {submitMessage && (
          <p className={`website-announcement-feedback website-announcement-feedback-${submitStatus}`} role={submitStatus === "error" ? "alert" : "status"}>
            {submitMessage}
          </p>
        )}
      </form>
      </div>
    </Modal>
  );
};

export default WebsiteAnnouncement;
